/**
 * @file: useVoiceDictation.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Speech-to-text for a text input: records with `useVoiceRecording`, hands the
 * clip to the backend recognizer, and reports the transcript through `onText`.
 * The host owns the UI — typically a mic button that calls `start`, then the
 * shared `VoiceRecordingBar` (fed by `meter$`) flanked by `cancel` / `complete`.
 *
 * The browser's own recorder writes webm, which the recognizer rejects, so on
 * web the clip comes from `useWavRecording` instead, as WAV.
 */

import { useVoiceRecording } from '@euler/functions/useVoiceRecording';
import { useWavRecording } from '@euler/functions/useWavRecording';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useRef, useState } from 'react';
import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';

export const useVoiceDictation = ({
  onText,
}: {
  /** Receives the recognized text; empty results are swallowed. */
  onText: (text: string) => void;
}) => {
  const { voiceService } = useServiceFactory();
  const meter$ = useCreation(() => new BehaviorSubject(0), []);
  const [recording, setRecording] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const nativeRecording = useVoiceRecording({
    onMeter: level => meter$.next(level),
  });
  const wavRecording = useWavRecording({
    onMeter: level => meter$.next(level),
  });
  const voiceRecording = Platform.OS === 'web' ? wavRecording : nativeRecording;
  // Guards a double `complete` (the button can be hit twice before the
  // recorder resolves), which would stop an already-unloaded recording.
  const completingRef = useRef(false);

  const start = usePersistFn(async () => {
    try {
      meter$.next(0);
      await voiceRecording.start();
      setRecording(true);
    } catch (e) {
      console.error('voice recording start failed', e);
    }
  });

  const cancel = usePersistFn(() => {
    setRecording(false);
    meter$.next(0);
    voiceRecording.cancel().catch(onErrorIgnore);
  });

  const complete = usePersistFn(async () => {
    if (completingRef.current) return;
    completingRef.current = true;
    setRecording(false);
    meter$.next(0);
    try {
      const recorded = await voiceRecording.stop().catch(e => {
        console.error('voice recording stop failed', e);
        return null;
      });
      if (!recorded) return;
      setRecognizing(true);
      const { text } = await voiceService.recognize({
        uri: recorded.uri,
        format: recorded.format,
        sampleRate: recorded.sampleRate,
      });
      if (text) onText(text);
    } catch (e) {
      console.error('voice recognize failed', e);
    } finally {
      setRecognizing(false);
      completingRef.current = false;
    }
  });

  return {
    supported: true,
    recording,
    /** True while the clip is with the recognizer. */
    recognizing,
    /** Normalized 0..1 mic level, for `VoiceRecordingBar`. */
    meter$,
    start,
    cancel,
    complete,
  };
};
